import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  AutomationActivityItemDto,
  AutomationActivityResponseDto,
  AutomationChannel,
  AutomationDepartment,
  AutomationStatus,
  fetchAutomationActivity,
} from '../../api/AutomationActivityApi';

export interface AutomationActivityFeedProps {
  defaultDepartment?: AutomationDepartment | 'all';
  defaultStatus?: AutomationStatus | 'all';
  ticketId?: number;
  pageSize?: number;
  title?: string;
  subtitle?: string;
  compact?: boolean;
}

type DatePreset = 'all' | 'today' | '7d' | '30d' | 'custom';

function statusChipColor(
  status: AutomationStatus,
): 'success' | 'info' | 'warning' | 'error' {
  switch (status) {
    case 'sent':
      return 'success';
    case 'drafted':
      return 'info';
    case 'skipped':
      return 'warning';
    case 'failed':
      return 'error';
  }
}

function statusDisplayLabel(status: AutomationStatus): string {
  switch (status) {
    case 'sent':
      return 'Sent';
    case 'drafted':
      return 'Drafted (Outlook)';
    case 'skipped':
      return 'Skipped';
    case 'failed':
      return 'Failed';
  }
}

function channelDisplayLabel(channel: AutomationChannel): string {
  switch (channel) {
    case 'email':
      return 'Email';
    case 'sms':
      return 'SMS';
    case 'portal':
      return 'Portal';
    case 'graph_draft':
      return 'Outlook Draft';
  }
}

function departmentDisplayLabel(dept: AutomationDepartment): string {
  switch (dept) {
    case 'intake':
      return 'Intake';
    case 'calendar':
      return 'Calendar';
    case 'closing':
      return 'Closing';
    case 'general':
      return 'General';
  }
}

function sourceDisplayLabel(
  source: AutomationActivityItemDto['sourceTable'],
): string {
  switch (source) {
    case 'automation_activity':
      return 'Live Send Log';
    case 'user_note':
      return 'Case Note';
    case 'message':
      return 'Client Message';
    case 'offer':
      return 'Offer Conveyance';
    case 'court_event':
      return 'Court Event Stamp';
    case 'ticket_stamp':
      return 'Case Timestamp';
  }
}

function looksLikeHtml(text: string): boolean {
  return /<\s*(html|body|div|p|br|table|tr|td|strong|em|ul|ol|li|span|a|h[1-6])\b/i.test(
    text,
  );
}

const AutomationActivityFeed: React.FC<AutomationActivityFeedProps> = ({
  defaultDepartment = 'all',
  defaultStatus = 'all',
  ticketId,
  pageSize = 50,
  title = 'Automation Activity Feed',
  subtitle = 'What the system sent, drafted, skipped, or failed — when, to whom, on which case or lead, and the exact content where stored.',
  compact = false,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<AutomationStatus | 'all'>(
    defaultStatus,
  );
  const [selectedDepartment, setSelectedDepartment] = useState<
    AutomationDepartment | 'all'
  >(defaultDepartment);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);

  const [data, setData] = useState<AutomationActivityResponseDto | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] =
    useState<AutomationActivityItemDto | null>(null);

  const applyDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    setOffset(0);
    const now = new Date();
    const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

    if (preset === 'all') {
      setDateFrom('');
      setDateTo('');
    } else if (preset === 'today') {
      const today = toIsoDate(now);
      setDateFrom(today);
      setDateTo(today);
    } else if (preset === '7d') {
      const from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      setDateFrom(toIsoDate(from));
      setDateTo(toIsoDate(now));
    } else if (preset === '30d') {
      const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setDateFrom(toIsoDate(from));
      setDateTo(toIsoDate(now));
    }
  };

  const loadFeed = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchAutomationActivity({
        search: appliedSearch || undefined,
        type: selectedType !== 'all' ? selectedType : undefined,
        status: selectedStatus,
        department: selectedDepartment,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        ticketId,
        limit: pageSize,
        offset,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  }, [
    appliedSearch,
    selectedType,
    selectedStatus,
    selectedDepartment,
    dateFrom,
    dateTo,
    ticketId,
    pageSize,
    offset,
  ]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setOffset(0);
    setAppliedSearch(searchInput.trim());
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setAppliedSearch('');
    setSelectedType('all');
    setSelectedStatus(defaultStatus);
    setSelectedDepartment(defaultDepartment);
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setOffset(0);
  };

  const statusCounts = data?.statusCounts ?? {
    sent: 0,
    drafted: 0,
    skipped: 0,
    failed: 0,
  };
  const totalAllStatuses =
    statusCounts.sent +
    statusCounts.drafted +
    statusCounts.skipped +
    statusCounts.failed;

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        sx={{ mb: 2, gap: 1.5 }}
      >
        <Box>
          <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" onClick={loadFeed} disabled={loading}>
            Refresh
          </Button>
          <Button size="small" variant="text" onClick={handleClearFilters}>
            Reset filters
          </Button>
        </Stack>
      </Stack>

      {/* Status quick-filter chips */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Chip
          label={`All (${totalAllStatuses})`}
          color={selectedStatus === 'all' ? 'primary' : 'default'}
          variant={selectedStatus === 'all' ? 'filled' : 'outlined'}
          onClick={() => {
            setSelectedStatus('all');
            setOffset(0);
          }}
        />
        <Chip
          label={`Sent (${statusCounts.sent})`}
          color={selectedStatus === 'sent' ? 'success' : 'default'}
          variant={selectedStatus === 'sent' ? 'filled' : 'outlined'}
          onClick={() => {
            setSelectedStatus('sent');
            setOffset(0);
          }}
        />
        <Chip
          label={`Drafted in Outlook (${statusCounts.drafted})`}
          color={selectedStatus === 'drafted' ? 'info' : 'default'}
          variant={selectedStatus === 'drafted' ? 'filled' : 'outlined'}
          onClick={() => {
            setSelectedStatus('drafted');
            setOffset(0);
          }}
        />
        <Chip
          label={`Skipped (${statusCounts.skipped})`}
          color={selectedStatus === 'skipped' ? 'warning' : 'default'}
          variant={selectedStatus === 'skipped' ? 'filled' : 'outlined'}
          onClick={() => {
            setSelectedStatus('skipped');
            setOffset(0);
          }}
        />
        <Chip
          label={`Failed (${statusCounts.failed})`}
          color={selectedStatus === 'failed' ? 'error' : 'default'}
          variant={selectedStatus === 'failed' ? 'filled' : 'outlined'}
          onClick={() => {
            setSelectedStatus('failed');
            setOffset(0);
          }}
        />
      </Stack>

      {/* Filter bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 240 }}
          >
            <TextField
              fullWidth
              size="small"
              label="Search case #, client, court, recipient, or subject"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <Button type="submit" variant="contained" size="small">
              Search
            </Button>
          </Box>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="activity-dept-label">Department</InputLabel>
            <Select
              labelId="activity-dept-label"
              label="Department"
              value={selectedDepartment}
              onChange={(e) => {
                setSelectedDepartment(
                  e.target.value as AutomationDepartment | 'all',
                );
                setOffset(0);
              }}
            >
              <MenuItem value="all">All departments</MenuItem>
              <MenuItem value="intake">Intake</MenuItem>
              <MenuItem value="calendar">Calendar</MenuItem>
              <MenuItem value="closing">Closing</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 230 }}>
            <InputLabel id="activity-type-label">Automation Type</InputLabel>
            <Select
              labelId="activity-type-label"
              label="Automation Type"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setOffset(0);
              }}
            >
              <MenuItem value="all">All types</MenuItem>
              {(data?.availableTypes ?? []).map((t) => (
                <MenuItem key={t.type} value={t.type}>
                  {t.label} ({t.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="activity-date-preset-label">Date Range</InputLabel>
            <Select
              labelId="activity-date-preset-label"
              label="Date Range"
              value={datePreset}
              onChange={(e) => applyDatePreset(e.target.value as DatePreset)}
            >
              <MenuItem value="all">All dates</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="7d">Past 7 days</MenuItem>
              <MenuItem value="30d">Past 30 days</MenuItem>
              <MenuItem value="custom">Custom dates</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDatePreset('custom');
              setDateFrom(e.target.value);
              setOffset(0);
            }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDatePreset('custom');
              setDateTo(e.target.value);
              setOffset(0);
            }}
            sx={{ width: 150 }}
          />
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data?.isCapped && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the latest {data.total.toLocaleString()} results. Narrow the filters to find earlier activity.
        </Alert>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>To Whom</TableCell>
              <TableCell>Case / Lead</TableCell>
              <TableCell>Subject / Summary</TableCell>
              <TableCell align="right">Content</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (data?.items ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No automation activity matches the selected filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (data?.items ?? []).map((item) => {
                const formattedDate = new Date(item.createdAt).toLocaleString(
                  'en-US',
                  {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  },
                );

                return (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{formattedDate}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sourceDisplayLabel(item.sourceTable)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.typeLabel}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={departmentDisplayLabel(item.department)}
                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        color={statusChipColor(item.status)}
                        label={statusDisplayLabel(item.status)}
                      />
                      {item.statusReason && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                           sx={{ display: 'block', mt: 0.5, maxWidth: 180 }}
                        >
                          {item.statusReason}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={channelDisplayLabel(item.channel)}
                      />
                    </TableCell>

                    <TableCell>
                      {item.recipientName && (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.recipientName}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-all' }}
                      >
                        {item.recipient || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {item.targetKind === 'lead' ? (
                        <Box>
                          <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            label="Lead (No Case Yet)"
                            sx={{ mb: 0.5 }}
                          />
                          {item.clientName && (
                            <Typography variant="body2">
                              {item.clientName}
                            </Typography>
                          )}
                          {item.leadRef && (
                            <Typography variant="caption" color="text.secondary">
                              {item.leadRef}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          {item.ticketGuid ? (
                            <Typography
                              variant="body2"
                              component={Link}
                              to={`/ticket?Ticket_guid=${encodeURIComponent(item.ticketGuid)}`}
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {item.caseNumber || `Case #${item.ticketId}`}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {item.caseNumber || '—'}
                            </Typography>
                          )}
                          {item.clientName && (
                            <Typography
                              variant="caption"
                              sx={{ display: 'block' }}
                              color="text.secondary"
                            >
                              {item.userGuid ? (
                                <Link
                                  to={`/user?User_guid=${encodeURIComponent(item.userGuid)}`}
                                  style={{ color: 'inherit' }}
                                >
                                  {item.clientName}
                                </Link>
                              ) : (
                                item.clientName
                              )}
                            </Typography>
                          )}
                          {item.courtName && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {item.courtName}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>

                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.subject || item.typeLabel}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        variant={item.content ? 'outlined' : 'text'}
                        onClick={() => setSelectedItem(item)}
                      >
                        {item.content ? 'View content' : 'Details'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {data && data.total > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2 }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {offset + 1}–{Math.min(offset + data.items.length, data.total)} of{' '}
            {data.total} activity entries
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
            >
              Previous
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={offset + pageSize >= data.total || loading}
              onClick={() => setOffset(offset + pageSize)}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      )}

      {/* Read-only Content Inspection Dialog */}
      <Dialog
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedItem && (
          <>
            <DialogTitle>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedItem.typeLabel}
                </Typography>
                <Chip
                  size="small"
                  color={statusChipColor(selectedItem.status)}
                  label={statusDisplayLabel(selectedItem.status)}
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>When:</strong>{' '}
                  {new Date(selectedItem.createdAt).toLocaleString('en-US')}
                </Typography>
                <Typography variant="body2">
                  <strong>Channel:</strong>{' '}
                  {channelDisplayLabel(selectedItem.channel)} (
                  {sourceDisplayLabel(selectedItem.sourceTable)})
                </Typography>
                <Typography variant="body2">
                  <strong>To Whom:</strong>{' '}
                  {selectedItem.recipientName
                    ? `${selectedItem.recipientName} (${selectedItem.recipient || 'no address recorded'})`
                    : selectedItem.recipient || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Case / Lead:</strong>{' '}
                  {selectedItem.targetKind === 'lead' ? (
                    `Lead (${selectedItem.leadRef || 'unpaid inquiry'})`
                  ) : selectedItem.ticketGuid ? (
                    <Link
                      to={`/ticket?Ticket_guid=${encodeURIComponent(selectedItem.ticketGuid)}`}
                    >
                      {selectedItem.caseNumber || `Case #${selectedItem.ticketId}`}
                    </Link>
                  ) : (
                    selectedItem.caseNumber || '—'
                  )}
                  {selectedItem.clientName ? ` — ${selectedItem.clientName}` : ''}
                  {selectedItem.courtName ? ` (${selectedItem.courtName})` : ''}
                </Typography>
                {selectedItem.subject && (
                  <Typography variant="body2">
                    <strong>Subject:</strong> {selectedItem.subject}
                  </Typography>
                )}
                {selectedItem.statusReason && (
                  <Alert
                    severity={
                      selectedItem.status === 'failed'
                        ? 'error'
                        : selectedItem.status === 'skipped'
                        ? 'warning'
                        : 'info'
                    }
                  >
                    <strong>Reason:</strong> {selectedItem.statusReason}
                  </Alert>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Stored Content
              </Typography>
              {selectedItem.content ? (
                looksLikeHtml(selectedItem.content) ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 0,
                      bgcolor: '#fafafa',
                      overflow: 'hidden',
                      borderRadius: 1,
                    }}
                  >
                    <iframe
                      sandbox=""
                      srcDoc={selectedItem.content}
                      title="Stored email content"
                      style={{
                        width: '100%',
                        height: 420,
                        border: 'none',
                        display: 'block',
                        backgroundColor: '#ffffff',
                      }}
                    />
                  </Paper>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: '#fafafa',
                      maxHeight: 420,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                    }}
                  >
                    {selectedItem.content}
                  </Paper>
                )
              ) : (
                <Alert severity="info">
                  Exact message body was not stored for this historical timestamp
                  record ({sourceDisplayLabel(selectedItem.sourceTable)}). All new
                  automated sends and Outlook drafts store their full content in
                  the Automation Activity log.
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedItem(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AutomationActivityFeed;
